<div class="data-feed-monitor">
    <!-- Header -->
    <div class="data-feed-header actions-bar">
        <div>
            <h1>Data Feed Monitor</h1>
            <p class="subtitle">Live status, progress and trace information for every data feed upload.</p>
        </div>
        <div class="header-actions">
            <div class="live-status" [class.paused]="paused" [class.has-error]="!!pollError">
                <span class="live-dot" aria-hidden="true"></span>
                <span class="live-label">{{ paused ? 'Paused' : 'Live' }}</span>
                <span class="live-updated" *ngIf="lastUpdated !== null">updated {{ updatedAgo() }}</span>
                <mat-spinner *ngIf="refreshing && !initialLoading" [diameter]="14" [strokeWidth]="2"
                    aria-label="Refreshing"></mat-spinner>
            </div>
            <button mat-icon-button type="button" class="header-icon-btn" (click)="togglePaused()"
                [attr.aria-label]="paused ? 'Resume live updates' : 'Pause live updates'"
                [matTooltip]="paused ? 'Resume live updates' : 'Pause live updates'" [attr.aria-pressed]="paused">
                <mat-icon>{{ paused ? 'play_arrow' : 'pause' }}</mat-icon>
            </button>
            <button mat-icon-button type="button" class="header-icon-btn" (click)="refresh()"
                aria-label="Refresh now" matTooltip="Refresh now">
                <mat-icon>refresh</mat-icon>
            </button>
            <input type="file" #fileInput hidden accept=".zip,application/zip" (change)="onFileSelected($event)" />
            <button mat-raised-button color="accent" type="button" (click)="fileInput.click()"
                [disabled]="isUploading" class="upload-btn">
                <mat-icon *ngIf="!isUploading">upload_file</mat-icon>
                <mat-spinner *ngIf="isUploading" [diameter]="20" [strokeWidth]="2"></mat-spinner>
                {{ isUploading ? 'Uploading...' : 'Upload Files' }}
            </button>
        </div>
    </div>

    <!-- Screen-reader announcements for stage changes -->
    <div class="cdk-visually-hidden" aria-live="polite" aria-atomic="true">{{ liveMessage }}</div>

    <!-- Non-blocking poll error -->
    <div class="poll-error" role="alert" *ngIf="pollError">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <span>{{ pollError }}<ng-container *ngIf="view"> Showing the last data received.</ng-container></span>
        <button mat-button type="button" (click)="refresh()">Retry</button>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" role="group" aria-label="Upload summary">
        <div class="summary-card tone-paused" [class.summary-card-attention]="awaitingCount() > 0">
            <mat-icon class="summary-icon" aria-hidden="true">fact_check</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(awaitingCount())">{{ compact(awaitingCount()) }}</span>
            </span>
            <span class="summary-label">Awaiting confirmation</span>
        </div>
        <div class="summary-card tone-waiting">
            <mat-icon class="summary-icon" aria-hidden="true">schedule</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(waitingCount())">{{ compact(waitingCount()) }}</span>
            </span>
            <span class="summary-label">Waiting</span>
        </div>
        <div class="summary-card tone-processing">
            <mat-icon class="summary-icon" aria-hidden="true" [class.df-spin]="(summary?.Processing ?? 0) > 0">autorenew</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(summary?.Processing)">{{ compact(summary?.Processing) }}</span>
            </span>
            <span class="summary-label">Processing</span>
        </div>
        <div class="summary-card tone-success">
            <mat-icon class="summary-icon" aria-hidden="true">check_circle</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(summary?.Completed)">{{ compact(summary?.Completed) }}</span>
            </span>
            <span class="summary-label">Completed</span>
        </div>
        <div class="summary-card tone-warning">
            <mat-icon class="summary-icon" aria-hidden="true">warning</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(summary?.CompletedWithErrors)">{{ compact(summary?.CompletedWithErrors) }}</span>
            </span>
            <span class="summary-label">With errors</span>
        </div>
        <div class="summary-card tone-failed">
            <mat-icon class="summary-icon" aria-hidden="true">error</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(failedCount())">{{ compact(failedCount()) }}</span>
            </span>
            <span class="summary-label">Failed</span>
        </div>
        <div class="summary-card tone-neutral">
            <mat-icon class="summary-icon" aria-hidden="true">mark_email_unread</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading" [title]="exact(summary?.OpenDeadLetters)">{{ compact(summary?.OpenDeadLetters) }}</span>
            </span>
            <span class="summary-label">Open dead letters</span>
        </div>
        <div class="summary-card records-card">
            <mat-icon class="summary-icon" aria-hidden="true">dataset</mat-icon>
            <span class="summary-value">
                <span *ngIf="initialLoading" class="df-skeleton skeleton-value"></span>
                <span *ngIf="!initialLoading"
                    [title]="exact(summary?.RecordsSucceeded) + ' ok / ' + exact(summary?.RecordsFailed) + ' failed'">
                    <span class="records-ok">{{ compact(summary?.RecordsSucceeded) }}</span>
                    <span class="records-sep" aria-hidden="true">/</span>
                    <span class="records-failed">{{ compact(summary?.RecordsFailed) }}</span>
                </span>
            </span>
            <span class="summary-label">Records ok / failed</span>
        </div>
    </div>

    <!-- Uploads -->
    <div class="section file-uploads mat-mdc-card mdc-card">
        <div class="mdc-card-content">
            <!-- Toolbar -->
            <div class="feed-toolbar">
                <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                    <mat-icon matPrefix aria-hidden="true">search</mat-icon>
                    <input matInput type="search" placeholder="Search file name, import name or run id"
                        aria-label="Search uploads" [(ngModel)]="searchText" (ngModelChange)="onSearchChange($event)" />
                    <button mat-icon-button matSuffix type="button" *ngIf="searchText" (click)="clearSearch()"
                        aria-label="Clear search">
                        <mat-icon>close</mat-icon>
                    </button>
                </mat-form-field>

                <mat-button-toggle-group class="stage-filter" aria-label="Filter by stage" [value]="stageFilter"
                    (change)="onStageFilterChange($event.value)">
                    <mat-button-toggle *ngFor="let f of stageFilters" [value]="f.value">{{ f.label }}</mat-button-toggle>
                </mat-button-toggle-group>

                <mat-form-field appearance="outline" class="window-field" subscriptSizing="dynamic">
                    <mat-select [(value)]="windowHours" aria-label="Time window">
                        <mat-option *ngFor="let w of windowOptions" [value]="w.hours">{{ w.label }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="table-meta" *ngIf="!initialLoading && view">
                Showing {{ shownRange }} of {{ exact(matchingUploads) }} uploads
                <ng-container *ngIf="isFiltered"> (filtered from {{ exact(totalUploads) }})</ng-container>
                <ng-container *ngIf="view.EtlConfigCode"> &middot; config <code>{{ view.EtlConfigCode }}</code></ng-container>
                <ng-container *ngIf="hasActiveUploads && !paused"> &middot; refreshing every 3s</ng-container>
                <ng-container *ngIf="view.Truncated"> &middot; <span class="truncated-note">only the most recent uploads of this window are listed; choose a shorter window to see the rest</span></ng-container>
            </div>

            <!-- First-load skeleton -->
            <div class="table-scroll" *ngIf="initialLoading" aria-busy="true" aria-label="Loading uploads">
                <div class="skeleton-row" *ngFor="let r of skeletonRows">
                    <span class="df-skeleton" style="width: 28%"></span>
                    <span class="df-skeleton" style="width: 12%"></span>
                    <span class="df-skeleton" style="width: 20%"></span>
                    <span class="df-skeleton" style="width: 14%"></span>
                    <span class="df-skeleton" style="width: 10%"></span>
                </div>
            </div>

            <!-- First-load failure -->
            <div class="no-data load-error" *ngIf="!initialLoading && !view && pollError">
                <mat-icon aria-hidden="true">cloud_off</mat-icon>
                <p>The data feed could not be loaded.</p>
                <button mat-stroked-button color="primary" type="button" (click)="refresh()">Try again</button>
            </div>

            <ng-container *ngIf="!initialLoading && view">
                <div class="table-scroll" *ngIf="rows.length > 0; else noRows" [class.page-loading]="pageLoading"
                    [attr.aria-busy]="pageLoading">
                    <table mat-table [dataSource]="rows" [trackBy]="trackByKey" class="uploads-table">
                        <!-- File -->
                        <ng-container matColumnDef="file">
                            <th mat-header-cell *matHeaderCellDef>File</th>
                            <td mat-cell *matCellDef="let u">
                                <div class="file-cell">
                                    <span class="file-name" [title]="u.FileName || u.DisplayName || ''">{{ u.FileName || u.DisplayName || '(unnamed)' }}</span>
                                    <span class="display-name" *ngIf="u.DisplayName && u.DisplayName !== u.FileName"
                                        [title]="u.DisplayName">{{ u.DisplayName }}</span>
                                    <span class="people-line" *ngIf="u.UploadedBy || u.UploadedByName" [title]="u.UploadedBy || ''">
                                        <mat-icon aria-hidden="true">person</mat-icon>uploaded by {{ person(u.UploadedByName, u.UploadedBy) }}
                                    </span>
                                    <span class="run-id" *ngIf="u.RunId">
                                        <code [title]="u.RunId">{{ u.RunId }}</code>
                                        <button mat-icon-button type="button" class="tiny-btn"
                                            (click)="copy(u.RunId, 'Run id', $event)" aria-label="Copy run id"
                                            matTooltip="Copy run id">
                                            <mat-icon>content_copy</mat-icon>
                                        </button>
                                    </span>
                                </div>
                            </td>
                        </ng-container>

                        <!-- Stage -->
                        <ng-container matColumnDef="stage">
                            <th mat-header-cell *matHeaderCellDef>Stage</th>
                            <td mat-cell *matCellDef="let u">
                                <div class="stage-badges">
                                    <ng-container *ngIf="stageMeta(u) as meta">
                                        <span class="df-badge" [ngClass]="'tone-' + meta.tone">
                                            <mat-icon aria-hidden="true" [class.df-spin]="meta.spin">{{ meta.icon }}</mat-icon>
                                            {{ meta.label }}
                                        </span>
                                    </ng-container>
                                    <span class="df-badge pending-chip" *ngIf="pendingControl(u) as pending"
                                        [ngClass]="'tone-' + pending.tone" [matTooltip]="pending.tooltip">
                                        <mat-icon aria-hidden="true">pending</mat-icon>
                                        {{ pending.label }}
                                    </span>
                                </div>
                                <div class="stage-reason" *ngIf="u.StageReason || u.FailureReason"
                                    [matTooltip]="u.StageReason || u.FailureReason">
                                    {{ u.StageReason || u.FailureReason }}
                                </div>
                                <div class="decision-line" *ngIf="u.ConfirmedBy || u.ConfirmedByName" [title]="u.ConfirmedBy || ''">
                                    <mat-icon aria-hidden="true">fact_check</mat-icon>
                                    Confirmed by {{ person(u.ConfirmedByName, u.ConfirmedBy) }}<ng-container *ngIf="u.ConfirmedAt"> &middot; {{ u.ConfirmedAt | date:'MMM d, h:mm a' }}</ng-container>
                                </div>
                            </td>
                        </ng-container>

                        <!-- Progress -->
                        <ng-container matColumnDef="progress">
                            <th mat-header-cell *matHeaderCellDef>Progress</th>
                            <td mat-cell *matCellDef="let u">
                                <div class="progress-cell" *ngIf="u.Stage === 'Processing'; else stoppedProgress">
                                    <span class="progress-text" *ngIf="u.EntitiesTotal > 0">
                                        Entity {{ currentEntityIndex(u) }} of {{ u.EntitiesTotal }}<ng-container
                                            *ngIf="u.CurrentEntity"> &mdash; <strong>{{ u.CurrentEntity }}</strong></ng-container>
                                    </span>
                                    <span class="progress-text" *ngIf="u.EntitiesTotal === 0">Starting&hellip;</span>
                                    <mat-progress-bar [mode]="u.EntitiesTotal > 0 ? 'determinate' : 'indeterminate'"
                                        [value]="entityProgress(u)"
                                        [attr.aria-label]="'Entities processed for ' + (u.FileName || u.RunId)"></mat-progress-bar>
                                </div>
                                <!-- Paused / cancelled part-way: a static bar showing where it stopped. -->
                                <ng-template #stoppedProgress>
                                    <div class="progress-cell halted" *ngIf="isHalted(u); else recordCounts"
                                        [ngClass]="'halted-' + stageMeta(u).tone">
                                        <span class="progress-text">
                                            {{ stageMeta(u).label }} at entity {{ currentEntityIndex(u) }} of {{ u.EntitiesTotal }}<ng-container
                                                *ngIf="u.CurrentEntity"> &mdash; <strong>{{ u.CurrentEntity }}</strong></ng-container>
                                        </span>
                                        <mat-progress-bar mode="determinate" [value]="entityProgress(u)"
                                            [attr.aria-label]="'Entities processed before ' + (u.FileName || u.RunId) + ' stopped'"></mat-progress-bar>
                                    </div>
                                </ng-template>
                                <ng-template #recordCounts>
                                    <span class="record-counts" *ngIf="u.RecordsSucceeded !== null || u.RecordsFailed !== null; else noCounts">
                                        <span class="records-ok">{{ (u.RecordsSucceeded ?? 0) | number }} ok</span>
                                        <span class="records-failed" *ngIf="(u.RecordsFailed ?? 0) > 0">
                                            <mat-icon aria-hidden="true">cancel</mat-icon>{{ u.RecordsFailed | number }} failed
                                        </span>
                                    </span>
                                    <ng-template #noCounts><span class="muted">&ndash;</span></ng-template>
                                </ng-template>
                            </td>
                        </ng-container>

                        <!-- Uploaded -->
                        <ng-container matColumnDef="uploaded">
                            <th mat-header-cell *matHeaderCellDef>Uploaded</th>
                            <td mat-cell *matCellDef="let u" class="nowrap">
                                <ng-container *ngIf="u.UploadedAt || u.QueuedAt; else dash">{{ (u.UploadedAt || u.QueuedAt) | date:'MMM d, h:mm:ss a' }}</ng-container>
                            </td>
                        </ng-container>

                        <!-- Started -->
                        <ng-container matColumnDef="started">
                            <th mat-header-cell *matHeaderCellDef>Started</th>
                            <td mat-cell *matCellDef="let u" class="nowrap">
                                <ng-container *ngIf="u.StartedAt; else dash">{{ u.StartedAt | date:'MMM d, h:mm:ss a' }}</ng-container>
                            </td>
                        </ng-container>

                        <!-- Duration -->
                        <ng-container matColumnDef="duration">
                            <th mat-header-cell *matHeaderCellDef>Duration</th>
                            <td mat-cell *matCellDef="let u" class="nowrap" [class.live-duration]="u.Stage === 'Processing'">
                                {{ duration(u) }}
                            </td>
                        </ng-container>

                        <!-- Attempts -->
                        <ng-container matColumnDef="attempts">
                            <th mat-header-cell *matHeaderCellDef>Attempts</th>
                            <td mat-cell *matCellDef="let u">
                                <span class="attempts" [class.retried]="u.Attempts > 1"
                                    [matTooltip]="u.Attempts > 1 ? 'Retried ' + (u.Attempts - 1) + ' time(s)' : ''">
                                    <mat-icon *ngIf="u.Attempts > 1" aria-hidden="true">replay</mat-icon>{{ u.Attempts }}
                                </span>
                            </td>
                        </ng-container>

                        <!-- Actions -->
                        <ng-container matColumnDef="actions">
                            <th mat-header-cell *matHeaderCellDef>Actions</th>
                            <td mat-cell *matCellDef="let u">
                                <div class="action-buttons">
                                    <!-- The wrapper carries the tooltip so it still shows while the button is disabled. -->
                                    <ng-container *ngIf="u.Stage === 'AwaitingConfirmation' && u.UploadId">
                                        <button mat-flat-button color="primary" type="button" class="action-review"
                                            matTooltip="Review what this import will do, then queue or discard it" matTooltipPosition="above"
                                            [disabled]="isUploadBusy(u)" (click)="reviewUpload(u, $event)"
                                            [attr.aria-label]="'Review and confirm import of ' + (u.FileName || 'upload')">
                                            <mat-icon>fact_check</mat-icon>
                                            Review
                                        </button>
                                        <button mat-icon-button type="button" class="action-icon action-control action-cancel"
                                            matTooltip="Discard &mdash; delete the staged file without importing" matTooltipPosition="above"
                                            [disabled]="isUploadBusy(u)" (click)="discardUpload(u, $event)"
                                            [attr.aria-label]="'Discard upload ' + (u.FileName || 'upload')">
                                            <mat-icon>delete</mat-icon>
                                        </button>
                                    </ng-container>
                                    <!-- Run controls first: they must stay on the first line of the cell. -->
                                    <button mat-icon-button type="button" class="action-icon action-control action-pause" *ngIf="u.CanPause && u.RunId"
                                        matTooltip="Pause &mdash; stops now, keeps progress; Start continues it later" matTooltipPosition="above"
                                        [disabled]="runControl.isBusy(u.RunId)"
                                        (click)="controlRun('pause', u, $event)"
                                        [attr.aria-label]="'Pause import of ' + (u.FileName || u.DisplayName || 'upload')">
                                        <mat-icon>pause_circle</mat-icon>
                                    </button>
                                    <button mat-icon-button type="button" class="action-icon action-control" *ngIf="u.CanResume && u.RunId"
                                        matTooltip="Start &mdash; continue from where it stopped" matTooltipPosition="above"
                                        [disabled]="runControl.isBusy(u.RunId)"
                                        (click)="controlRun('resume', u, $event)"
                                        [attr.aria-label]="'Start import of ' + (u.FileName || u.DisplayName || 'upload') + ' again'">
                                        <mat-icon>play_circle</mat-icon>
                                    </button>
                                    <button mat-icon-button type="button" class="action-icon action-control action-cancel" *ngIf="u.CanCancel && u.RunId"
                                        matTooltip="Stop &mdash; cancel this import for good" matTooltipPosition="above"
                                        [disabled]="runControl.isBusy(u.RunId)"
                                        (click)="controlRun('cancel', u, $event)"
                                        [attr.aria-label]="'Stop (cancel) import of ' + (u.FileName || u.DisplayName || 'upload')">
                                        <mat-icon>stop_circle</mat-icon>
                                    </button>
                                    <span *ngIf="u.Stage !== 'AwaitingConfirmation'" [matTooltip]="u.RunId ? 'Trace: entities, errors and timeline' : (u.StageReason || 'No run has been created for this upload yet')"
                                        matTooltipPosition="above">
                                        <button mat-icon-button color="primary" type="button" class="action-icon"
                                            [disabled]="!u.RunId" (click)="openTrace(u, $event)"
                                            [attr.aria-label]="'Open trace for ' + (u.FileName || u.DisplayName || 'upload')">
                                            <mat-icon>troubleshoot</mat-icon>
                                        </button>
                                    </span>
                                    <button mat-icon-button color="primary" type="button" class="action-icon" *ngIf="hasPackageDetails(u)"
                                        matTooltip="Package details &mdash; the manifest and what the import was going to do" matTooltipPosition="above"
                                        [disabled]="isOpeningDetails(u)" (click)="openPackageDetails(u, $event)"
                                        [attr.aria-label]="'Package details of ' + (u.FileName || 'upload')">
                                        <mat-icon>description</mat-icon>
                                    </button>
                                    <button mat-icon-button color="primary" type="button" class="action-icon" *ngIf="u.CanDownload && u.UploadId"
                                        matTooltip="Download the uploaded package" matTooltipPosition="above"
                                        [disabled]="isDownloading(u)" (click)="downloadPackage(u, $event)"
                                        [attr.aria-label]="'Download ' + (u.FileName || 'package')">
                                        <mat-icon>download</mat-icon>
                                    </button>
                                    <button mat-icon-button color="primary" type="button" class="action-icon" *ngIf="canOpenIaReport(u)"
                                        matTooltip="IA copy report" matTooltipPosition="above"
                                        (click)="openIaCopyReportDialog(u, $event)"
                                        [attr.aria-label]="'Open IA copy report for ' + (u.FileName || u.DisplayName || 'upload')">
                                        <mat-icon>summarize</mat-icon>
                                    </button>
                                </div>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                            [class.clickable]="!!row.RunId"
                            [class.row-awaiting]="row.Stage === 'AwaitingConfirmation'"
                            [class.row-active]="row.Stage === 'Processing'"
                            [attr.tabindex]="row.RunId ? 0 : null"
                            [attr.aria-label]="row.RunId ? 'Open trace for ' + (row.FileName || row.DisplayName || row.RunId) : null"
                            (click)="onRowActivate(row, $event)"
                            (keydown.enter)="onRowActivate(row, $event)"></tr>
                    </table>
                </div>
                <mat-paginator *ngIf="matchingUploads > 0" class="feed-paginator" [length]="matchingUploads"
                    [pageIndex]="pageIndex" [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
                    [disabled]="pageLoading" showFirstLastButtons (page)="onPage($event)"
                    aria-label="Select page of uploads"></mat-paginator>
                <ng-template #noRows>
                    <div class="no-data">
                        <mat-icon aria-hidden="true">inbox</mat-icon>
                        <p *ngIf="totalUploads === 0">No uploads in the selected window.</p>
                        <p *ngIf="totalUploads > 0">No uploads match the current filters.</p>
                    </div>
                </ng-template>
            </ng-container>
        </div>
    </div>
</div>

<ng-template #dash><span class="muted">&ndash;</span></ng-template>
